import React, { useState } from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogProps,
  DialogTitle,
  Grid,
  Step,
  StepLabel,
  Stepper,
} from '@material-ui/core';
import { ArrowBack, Cancel, Done } from '@material-ui/icons';
import { Field, Form, Formik } from 'formik';
import * as yup from 'yup';
import { FormikTextFieldComponent } from 'components/Field';
import { IJournalEquipmentItem, IEquipment } from '../../interfaces';
import JournalEquipmentItemForm from './JournalEquipmentItemForm';

interface IProps {
  equipmentItem: IJournalEquipmentItem | null;
  disabled?: boolean;
  onClose(): void;
  onWriteOff(countWrittenOff: number): void | Promise<void>;
  onAddReplacement(newEquipment: IJournalEquipmentItem): void | Promise<void>;
}

interface IWriteOffFormValues {
  countWrittenOff: number;
  equipmentName?: string;
  equipmentCurrentCount?: number;
}

interface INewEquipmentFormValues {
  equipment: IEquipment | null;
  equipmentCount: number;
  equipmentCountry: string;
  equipmentModel: string;
  equipmentSerialNumberItem: string;
  equipmentSerialNumber: string[];
  equipmentCost: number;
  dateStart: Date | null;
  monthCount: number;
  comment: string;
  guaranteeMonth: number;
  equipmentTypeName?: string;
}

const JournalEquipmentItemWriteOffDialog: React.FC<IProps & Omit<DialogProps, 'onClose'>> = ({
  open,
  equipmentItem,
  disabled = false,
  onClose,
  onWriteOff,
  onAddReplacement,
}) => {
  const [activeStep, setActiveStep] = useState(0);

  const steps = ['Списание оборудования', 'Добавление нового оборудования'];

  const writeOffValidationSchema = yup.object().shape({
    countWrittenOff: yup
      .number()
      .required('Обязательное поле')
      .min(1, 'Минимум 1')
      .max(equipmentItem?.equipmentCount || 0, `Максимум ${equipmentItem?.equipmentCount || 0}`),
  });

  const newEquipmentValidationSchema = yup.object().shape({
    equipment: yup
      .object()
      .required('Обязательное поле')
      .nullable()
      .shape({ equipmentId: yup.string().required('Обязательное поле') }),
    equipmentCount: yup.number().required('Обязательное поле').min(1, 'Минимум 1'),
    equipmentCountry: yup.string().required('Обязательное поле'),
    equipmentModel: yup.string().required('Обязательное поле'),
    equipmentSerialNumber: yup.array().of(yup.string()),
    equipmentCost: yup.number().required('Обязательное поле'),
    dateStart: yup.date().nullable().typeError('Укажите корректную дату').required('Обязательное поле'),
    monthCount: yup.number().required('Обязательное поле'),
    comment: yup.string().nullable().required('Обязательное поле'),
    guaranteeMonth: yup.number().min(1, 'Обязательное поле').required('Обязательное поле'),
  });

  const handleNext = async (values: IWriteOffFormValues | INewEquipmentFormValues) => {
    if (activeStep === 0) {
      const writeOffValues = values as IWriteOffFormValues;
      // Вызываем API списания при переходе на шаг 2
      await onWriteOff(writeOffValues.countWrittenOff);
      setActiveStep(1);
    } else {
      const newEquipmentValues = values as INewEquipmentFormValues;
      const { equipment, equipmentSerialNumber, equipmentSerialNumberItem, equipmentTypeName, ...props } = newEquipmentValues;
      await onAddReplacement({
        equipmentId: equipment?.equipmentId,
        equipmentTypeId: equipmentItem?.equipmentTypeId,
        equipmentSerialNumber: equipmentSerialNumberItem
          ? [...equipmentSerialNumber, equipmentSerialNumberItem]
          : equipmentSerialNumber,
        ...props,
      });
    }
  };

  const handleBack = () => {
    setActiveStep(0);
  };

  const handleClose = () => {
    setActiveStep(0);
    onClose();
  };

  if (!equipmentItem) return null;

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth scroll="body">
      <DialogTitle>
        <b>Замена оборудования</b>
      </DialogTitle>

      <DialogContent dividers>
        <Stepper activeStep={activeStep} alternativeLabel>
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>

        {activeStep === 0 && (
          <Formik<IWriteOffFormValues>
            onSubmit={handleNext}
            validationSchema={writeOffValidationSchema}
            initialValues={{
              countWrittenOff: 0,
              equipmentName: equipmentItem.equipmentName || '',
              equipmentCurrentCount: equipmentItem.equipmentCount || 0,
            }}
            enableReinitialize
          >
            {() => (
              <Form>
                <Grid container spacing={2} style={{ marginTop: 16 }}>
                  <Grid item xs={12}>
                    <Field
                      name="equipmentName"
                      variant="filled"
                      fullWidth
                      label="Оборудование"
                      disabled={disabled}
                      InputProps={{ readOnly: true }}
                      component={FormikTextFieldComponent}
                    />
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <Field
                      name="equipmentCurrentCount"
                      variant="filled"
                      fullWidth
                      label="Текущее количество"
                      disabled={disabled}
                      InputProps={{ readOnly: true }}
                      component={FormikTextFieldComponent}
                    />
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <Field
                      name="countWrittenOff"
                      type="number"
                      variant="filled"
                      fullWidth
                      label="Количество списанного оборудования"
                      disabled={disabled}
                      component={FormikTextFieldComponent}
                    />
                  </Grid>
                </Grid>

                <DialogActions style={{ marginTop: 16, padding: 0 }}>
                  <Button variant="outlined" onClick={handleClose} disabled={disabled} startIcon={<Cancel />}>
                    Отмена
                  </Button>

                  <Button color="primary" variant="contained" type="submit" disabled={disabled}>
                    Далее
                  </Button>
                </DialogActions>
              </Form>
            )}
          </Formik>
        )}

        {activeStep === 1 && (
          <Formik<INewEquipmentFormValues>
            onSubmit={handleNext}
            validationSchema={newEquipmentValidationSchema}
            initialValues={{
              equipment: null,
              equipmentCount: 0,
              equipmentCountry: '',
              equipmentModel: '',
              equipmentSerialNumberItem: '',
              equipmentSerialNumber: [],
              equipmentCost: 0,
              dateStart: null,
              monthCount: 0,
              comment: '',
              guaranteeMonth: 0,
              equipmentTypeName: equipmentItem.equipmentTypeName || '',
            }}
            enableReinitialize
          >
            <Form>
              <JournalEquipmentItemForm
                disabled={disabled}
                equipmentTypeId={equipmentItem.equipmentTypeId}
              />

              <DialogActions style={{ marginTop: 16, padding: 0 }}>
                <Button variant="outlined" onClick={handleBack} disabled={disabled} startIcon={<ArrowBack />}>
                  Назад
                </Button>

                <Button variant="outlined" onClick={handleClose} disabled={disabled} startIcon={<Cancel />}>
                  Отмена
                </Button>

                <Button color="primary" variant="contained" type="submit" disabled={disabled} startIcon={<Done />}>
                  Сохранить
                </Button>
              </DialogActions>
            </Form>
          </Formik>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default JournalEquipmentItemWriteOffDialog;
